import { getAllPanelLiveStates } from '@/stores/panel-state-registry'
import { useLanguageStore } from '@/stores/language-store'

/** Hints per session; Deepgram allows ~500 tokens and xAI 100 terms, the server re-caps. */
export const STT_HINT_LIMIT = 50
/** Room kept for the fixed vocabulary even when many tickers are on screen. */
const MAX_TICKERS = STT_HINT_LIMIT - 12

/** Ticker symbols only; anything else (names, notes, indicators) is never sent. */
const TICKER = /^[A-Z][A-Z.]{0,5}$/

/** Plain English trading and platform words the recognizer tends to mishear. */
const TRADING_TERMS = [
  'premarket',
  'VWAP',
  'float',
  'relative volume',
  'short interest',
  'gap and go',
  'EdgeScript',
  'halt',
  'dilution',
  'offering',
  'short squeeze',
]

function addTicker(out: Set<string>, value: unknown): void {
  if (typeof value === 'string' && TICKER.test(value)) out.add(value)
}

/**
 * Speech-to-text hints for a voice turn: the tickers on screen (chart
 * symbols, watchlist and scanner rows) plus a short trading vocabulary.
 * Only values matching the ticker pattern are taken from panels, so list
 * names and anything the user wrote never leave the browser. The English
 * vocabulary is left out when the AI language is Spanish.
 */
export function collectSttHints(): string[] {
  const tickers = new Set<string>()
  for (const state of getAllPanelLiveStates().values()) {
    addTicker(tickers, state.ticker)
    if (Array.isArray(state.tickers)) state.tickers.forEach((t) => addTicker(tickers, t))
    if (Array.isArray(state.cells)) {
      for (const cell of state.cells) addTicker(tickers, (cell as { ticker?: unknown })?.ticker)
    }
    // Scanner snapshot: a pipe table, ticker in the first column, header first.
    if (typeof state.top_rows === 'string') {
      for (const line of state.top_rows.split('\n').slice(1))
        addTicker(tickers, line.split(' | ')[0]?.trim())
    }
  }
  const hints = [...tickers].slice(0, MAX_TICKERS)
  if (useLanguageStore.getState().language === 'en') {
    for (const term of TRADING_TERMS) {
      if (hints.length < STT_HINT_LIMIT) hints.push(term)
    }
  }
  return hints
}
